import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import { AuthContext } from '@/context/auth-context'
import useMyProjectsData from './hooks/useMyProjectsData'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import MyProjectsTable from './components/MyProjectsTable'
import TrenerFilterModal from '@/filter-modals/components/TrenerFilterModal'

const resolveUserId = user => user?._id || user?.id || user?.userid || null

export default function MyProjects() {
	const { t } = useTranslation()
	const { user } = useContext(AuthContext)
	const [searchInput, setSearchInput] = useState('')
	const [selectedTrainerId, setSelectedTrainerId] = useState(null)
	const [isTrenerFilterOpen, setIsTrenerFilterOpen] = useState(false)
	const currentUserId = resolveUserId(user)

	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		clearError,
	} = useMyProjectsData()

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [clearError, error, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		setSearchInput(e.target.value)
	}

	const handleSearchSubmit = useCallback(() => {
		updateFilters({ searchText: searchInput.trim() })
	}, [searchInput, updateFilters])

	const handleApplyTrenerFilter = useCallback(trainerId => {
		setSelectedTrainerId(trainerId || null)
		updateFilters({ trainerId: trainerId || null })
		setIsTrenerFilterOpen(false)
	}, [updateFilters])

	const onlyMineActive = Boolean(
		currentUserId && selectedTrainerId && String(selectedTrainerId) === String(currentUserId)
	)

	const handleOnlyMine = useCallback(() => {
		if (!currentUserId) return
		if (onlyMineActive) {
			setSelectedTrainerId(null)
			updateFilters({ trainerId: null })
			return
		}
		setSelectedTrainerId(currentUserId)
		updateFilters({ trainerId: currentUserId })
	}, [currentUserId, onlyMineActive, updateFilters])

	const handleClearFilters = useCallback(() => {
		setSearchInput('')
		setSelectedTrainerId(null)
		updateFilters({
			searchText: '',
			status: null,
			trainerId: null,
			placowkaId: null,
		})
	}, [updateFilters])

	const buttons = [
		{
			key: 'search',
			label: t('myProjects.list.actions.search', { defaultValue: t('myTrainings.list.actions.search') }),
			onClick: handleSearchSubmit,
			className: 'px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition flex items-center justify-center',
		},
		{
			key: 'onlyMine',
			label: t('myProjects.list.actions.onlyMine', { defaultValue: 'Tylko moje' }),
			onClick: handleOnlyMine,
			className: onlyMineActive
				? 'px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition flex items-center justify-center'
				: 'px-4 py-2 bg-white text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-50 transition flex items-center justify-center',
		},
		{
			key: 'clearFilters',
			label: t('myProjects.list.actions.clearFilters', { defaultValue: 'Wyczyść filtry' }),
			onClick: handleClearFilters,
			className: 'px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition flex items-center justify-center',
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchInput}
				onChange={handleSearchChange}
				placeholder={t('myProjects.list.searchPlaceholder', { defaultValue: 'Szukaj projektów po nazwie lub trenerze...' })}
				buttons={buttons}
			/>

			<MyProjectsTable
				data={data}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				hasTrenerFilter={!!selectedTrainerId}
				onOpenTrenerFilter={() => setIsTrenerFilterOpen(true)}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={data.length}
				totalCount={totalCount}
			/>

			<TrenerFilterModal
				isOpen={isTrenerFilterOpen}
				onClose={() => setIsTrenerFilterOpen(false)}
				selectedTrainerId={selectedTrainerId}
				onApply={handleApplyTrenerFilter}
				title={t('myProjects.list.filters.trenerTitle', { defaultValue: 'Filtruj po trenerze' })}
				description={t('myProjects.list.filters.trenerDescription', {
					defaultValue: 'Wybierz trenera, aby pokazać projekty, do których jest przypisany.',
				})}
				applyLabel={t('myProjects.list.filters.apply', { defaultValue: 'Zastosuj' })}
				clearLabel={t('myProjects.list.filters.clear', { defaultValue: 'Wyczyść filtr' })}
			/>
		</>
	)
}
